import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Building2, User } from "lucide-react";

const inp = "mt-1.5 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass focus:outline-none";
const OPTIONAL = [
  ["region", "Province / State / Region"],
  ["gender_identity", "Gender identity"],
  ["race_ethnicity", "Racial / ethnic identity"],
  ["employment_status", "Employment status"],
  ["industry", "Industry / sector"],
  ["career_level", "Career / organizational level"],
  ["years_experience", "Years of work experience"],
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("profile"); // profile | group | code | confirm
  const [options, setOptions] = useState({});
  const [busy, setBusy] = useState(false);
  const [prof, setProf] = useState({
    first_name: "", last_name: "", country: "", age_range: "",
    region: "", gender_identity: "", race_ethnicity: "", employment_status: "",
    industry: "", industry_other: "", career_level: "", years_experience: "",
  });
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [match, setMatch] = useState(null);
  const [intake, setIntake] = useState({});
  const [orgMeta, setOrgMeta] = useState({});
  const [metaErr, setMetaErr] = useState("");

  useEffect(() => {
    api.get("/config/profile-options").then((r) => setOptions(r.data.options)).catch(() => {});
    api.get("/me/profile").then((r) => { if (r.data.profile) { setProf((p) => ({ ...p, ...r.data.profile })); setStep("group"); } }).catch(() => {});
  }, []);
  useEffect(() => {
    if (user && (prof.first_name === "" || prof.last_name === "")) {
      setProf((p) => ({ ...p, first_name: p.first_name || user.first_name || "", last_name: p.last_name || user.last_name || "" }));
    }
  }, [user]); // eslint-disable-line

  const opt = (k) => options[k] || [];
  const setP = (k) => (e) => setProf({ ...prof, [k]: e.target.value });

  const saveProfile = async () => {
    if (!prof.first_name.trim() || !prof.last_name.trim() || !prof.country || !prof.age_range) {
      toast.error("First name, last name, country and age range are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/me/profile", prof);
      await refresh();
      setStep("group");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const validate = async () => {
    setCodeErr(""); setBusy(true);
    try {
      const r = await api.post("/me/validate-campaign-code", { code: code.trim() });
      setMatch(r.data); setStep("confirm");
    } catch (e) { setCodeErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmJoin = async () => {
    if (match?.intake_fields?.length) { setStep("intake"); return; }
    await doJoin();
  };

  const doJoin = async () => {
    setBusy(true);
    try {
      await api.post("/me/join-campaign", { code: code.trim(), intake });
      await refresh();
      setStep("role");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setStep("code"); }
    finally { setBusy(false); }
  };

  const roleFields = () => Object.entries(match?.participant_config?.fields || {}).filter(([, f]) => f.state !== "off");

  const submitMeta = async () => {
    setMetaErr("");
    const missing = roleFields().find(([k, f]) => f.state === "required" && !(orgMeta[k] || "").toString().trim());
    if (missing) { setMetaErr(`${missing[1].label} is required.`); return; }
    setBusy(true);
    try { await api.post("/me/org-metadata", { metadata: orgMeta }); setStep("welcome"); }
    catch (e) { setMetaErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const goIndependent = async () => {
    setBusy(true);
    try { await api.post("/me/go-independent"); await refresh(); navigate("/consent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <HeaderNav />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-14 pb-20 cg-fade-up">
        {step === "profile" && (
          <div data-testid="onboarding-profile">
            <p className="cg-eyebrow mb-3">Step 1 · Your profile</p>
            <h1 className="font-serif text-4xl text-[#F3F4F6] mb-2">A little bit about you</h1>
            <p className="text-sm text-[#9CA3AF] mb-8 max-w-xl">These optional questions help us better understand the people who use Caesar's Ghost. You can choose "Prefer not to answer" any time.</p>
            <div className="cg-card p-7 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm text-[#9CA3AF]">First name</label><input data-testid="prof-first" value={prof.first_name} onChange={setP("first_name")} className={inp} /></div>
                <div><label className="text-sm text-[#9CA3AF]">Last name</label><input data-testid="prof-last" value={prof.last_name} onChange={setP("last_name")} className={inp} /></div>
              </div>
              <div><label className="text-sm text-[#9CA3AF]">Email</label><input value={user?.email || ""} disabled className={`${inp} text-[#6B7280] font-mono`} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#9CA3AF]">Country of residence</label>
                  <select data-testid="prof-country" value={prof.country} onChange={setP("country")} className={inp}>
                    <option value="">Select…</option>{opt("country").map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-[#9CA3AF]">Age range</label>
                  <select data-testid="prof-age" value={prof.age_range} onChange={setP("age_range")} className={inp}>
                    <option value="">Select…</option>{opt("age_range").map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <p className="cg-eyebrow pt-2">Optional. You can choose "Prefer not to answer."</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {OPTIONAL.map(([k, label]) => (
                  <div key={k}>
                    <label className="text-sm text-[#9CA3AF]">{label}</label>
                    {opt(k).length > 0 ? (
                      <select data-testid={`prof-${k}`} value={prof[k]} onChange={setP(k)} className={inp}>
                        <option value="">Prefer not to answer</option>{opt(k).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input data-testid={`prof-${k}`} value={prof[k]} onChange={setP(k)} className={inp} placeholder="Optional" />
                    )}
                  </div>
                ))}
              </div>
              {prof.industry === "Other" && (
                <div data-testid="industry-other-wrap">
                  <label className="text-sm text-[#9CA3AF]">Please tell us your industry or field</label>
                  <input data-testid="prof-industry_other" value={prof.industry_other} onChange={setP("industry_other")} className={inp} placeholder="Your industry or field" />
                </div>
              )}
              <button data-testid="prof-continue" disabled={busy} onClick={saveProfile}
                className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
                {busy ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === "group" && (
          <div data-testid="onboarding-group">
            <p className="cg-eyebrow mb-3">Step 2 · Participation</p>
            <h1 className="font-serif text-4xl text-[#F3F4F6] mb-6">Are you taking Caesar's Ghost as part of an organization or group?</h1>
            <p className="text-sm text-[#9CA3AF] mb-8 max-w-xl">A group might be an employer, association, leadership program, educational institution, research cohort, or family. If not, you can take it as an independent individual.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <button data-testid="group-yes" onClick={() => setStep("code")} className="cg-card p-7 text-left hover:border-brass border border-[var(--cg-border)] cg-scale-btn">
                <Building2 className="w-6 h-6 text-brass mb-3" />
                <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">Yes</h2>
                <p className="text-sm text-[#9CA3AF]">I have an organization code from my organization or group.</p>
              </button>
              <button data-testid="group-no" onClick={goIndependent} disabled={busy} className="cg-card p-7 text-left hover:border-brass border border-[var(--cg-border)] cg-scale-btn disabled:opacity-60">
                <User className="w-6 h-6 text-ghostslate mb-3" />
                <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">No</h2>
                <p className="text-sm text-[#9CA3AF]">Continue as an independent individual. Your results won't be part of any organization's combined results.</p>
              </button>
            </div>
          </div>
        )}

        {step === "code" && (
          <div data-testid="onboarding-code">
            <p className="cg-eyebrow mb-3">Step 3 · Organization code</p>
            <h1 className="font-serif text-4xl text-[#F3F4F6] mb-3">Enter your organization code</h1>
            <p className="text-sm text-[#9CA3AF] mb-8">It securely links your assessment to the right group.</p>
            <div className="cg-card p-7 space-y-4">
              <label htmlFor="code-input" className="cg-eyebrow block">Organization Code</label>
              <input id="code-input" data-testid="code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter your organization code"
                className={`${inp} font-mono uppercase`} />
              <p className="text-sm text-[#9CA3AF]">Use the code provided by your organization.</p>
              {codeErr && <p data-testid="code-error" className="text-sm text-terracotta">{codeErr}</p>}
              <div className="border border-brass/40 bg-[rgba(217,162,95,0.08)] rounded-lg p-4" data-testid="code-privacy-note">
                <p className="text-sm text-[#E8EAEE] leading-relaxed">Your Authority Portrait is personal to you. Your organization will not receive your individual answers or personal report. It will only receive combined results from participants when there are enough responses to protect individual privacy.</p>
              </div>
              <div className="flex gap-3">
                <button data-testid="code-back" onClick={() => setStep("group")} className="border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-3 rounded-full hover:border-brass">Go back</button>
                <button data-testid="code-validate" disabled={busy || !code.trim()} onClick={validate}
                  className="flex-1 bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
                  {busy ? "Checking…" : "Validate code"}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "confirm" && match && (
          <div data-testid="onboarding-confirm">
            <p className="cg-eyebrow mb-3">Step 3 · Confirm</p>
            <h1 className="font-serif text-4xl text-[#F3F4F6] mb-6">You are joining</h1>
            <div className="cg-card p-7 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-brass mt-0.5" />
                <div>
                  <p className="text-sm text-[#9CA3AF]">Group</p>
                  <p className="font-serif text-2xl text-[#F3F4F6] mb-3" data-testid="confirm-org">{match.organization_name}</p>
                  <p className="text-sm text-[#9CA3AF]">Campaign</p>
                  <p className="font-serif text-xl text-[#F3F4F6]" data-testid="confirm-campaign">{match.campaign_name}</p>
                  {match.current_wave_label && <p className="text-xs text-[#6B7280] font-mono mt-2">Assessment period: {match.current_wave_label}</p>}
                </div>
              </div>
            </div>
            <div className="cg-card p-6 mb-6 border-brass/40" data-testid="confirm-privacy">
              <p className="cg-eyebrow mb-3">Before you continue</p>
              <ul className="space-y-2 text-sm text-[#B9BDC7] leading-relaxed list-disc pl-4">
                <li>You will receive your own personal Authority Portrait.</li>
                <li>Your individual answers and personal report will not be shared with the organization or group that invited you.</li>
                <li>The organization or group will only see combined results when there are enough participants to protect individual privacy.</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button data-testid="confirm-continue" disabled={busy} onClick={confirmJoin}
                className="flex-1 bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
                {busy ? "Joining…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === "intake" && match && (
          <div data-testid="onboarding-intake">
            <p className="cg-eyebrow mb-3">Step 4 · {match.organization_name}</p>
            <h1 className="font-serif text-3xl text-[#F3F4F6] mb-2">A few questions from {match.organization_name}</h1>
            <p className="text-sm text-[#9CA3AF] mb-8 max-w-xl">These help {match.organization_name} understand its combined results. Your individual answers are never shown to them. Your organization or group will only see results for groups that are large enough to protect individual privacy. If a group is too small, its results will not be shown separately.</p>
            <div className="cg-card p-7 space-y-5">
              {match.intake_fields.map((f) => (
                <div key={f.key}>
                  <label className="text-sm text-[#9CA3AF]">{f.label}{f.required && <span className="text-brass"> *</span>}</label>
                  {f.type === "select" ? (
                    <select data-testid={`intake-${f.key}`} value={intake[f.key] || ""} onChange={(e) => setIntake({ ...intake, [f.key]: e.target.value })} className={inp}>
                      <option value="">Select…</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`intake-${f.key}`} value={intake[f.key] || ""} onChange={(e) => setIntake({ ...intake, [f.key]: e.target.value })} className={inp} placeholder={f.required ? "Required" : "Optional"} />
                  )}
                </div>
              ))}
              <div className="flex gap-3">
                <button data-testid="intake-back" onClick={() => setStep("confirm")} className="border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-3 rounded-full hover:border-brass">Go back</button>
                <button data-testid="intake-continue" disabled={busy || match.intake_fields.some((f) => f.required && !intake[f.key])} onClick={doJoin}
                  className="flex-1 bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-50">
                  {busy ? "Joining…" : "Continue"}
                </button>
              </div>
            </div>
          </div>
        )}
        {step === "role" && match && (
          <div data-testid="onboarding-role">
            <p className="cg-eyebrow mb-3">About your role · {match.organization_name}</p>
            <h1 className="font-serif text-3xl text-[#F3F4F6] mb-2">About your role</h1>
            <p className="text-sm text-[#9CA3AF] mb-8 max-w-xl">These questions help us understand patterns across your organization. Your individual Authority Portrait remains private.</p>
            <div className="cg-card p-7 space-y-5">
              {roleFields().map(([k, f]) => (
                <div key={k}>
                  <label className="text-sm text-[#9CA3AF]">{f.label}{f.state === "required" && <span className="text-brass"> *</span>}</label>
                  {(f.options || []).length > 0 ? (
                    <select data-testid={`role-${k}`} value={orgMeta[k] || ""} onChange={(e) => setOrgMeta({ ...orgMeta, [k]: e.target.value })} className={inp}>
                      <option value="">{f.state === "required" ? "Select…" : "Prefer not to say"}</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`role-${k}`} value={orgMeta[k] || ""} onChange={(e) => setOrgMeta({ ...orgMeta, [k]: e.target.value })} className={inp} placeholder={f.state === "required" ? "Required" : "Optional"} />
                  )}
                </div>
              ))}
              {roleFields().length === 0 && <p className="text-sm text-[#9CA3AF]">No additional information is required. You can continue.</p>}
              {metaErr && <p data-testid="role-error" className="text-sm text-terracotta">{metaErr}</p>}
              <button data-testid="role-continue" disabled={busy} onClick={submitMeta}
                className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
                {busy ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === "welcome" && match && (
          <div data-testid="onboarding-welcome">
            {match.participant_config?.welcome?.logo_url && (
              <img src={match.participant_config.welcome.logo_url} alt="" className="h-12 mb-6 object-contain" data-testid="welcome-logo" />
            )}
            <p className="cg-eyebrow mb-3">{match.organization_name}</p>
            <h1 className="font-serif text-4xl text-[#F3F4F6] mb-6" data-testid="welcome-heading">{match.participant_config?.welcome?.heading || "Welcome to Caesar's Ghost"}</h1>
            <div className="cg-card p-7 space-y-4 text-[#B9BDC7] leading-relaxed text-sm">
              {match.participant_config?.welcome?.message ? (
                <p data-testid="welcome-message" className="whitespace-pre-line">{match.participant_config.welcome.message}</p>
              ) : (
                <>
                  <p>{match.organization_name} has invited you to complete Caesar's Ghost, an assessment designed to help you understand where authority sits in consequential decision-making.</p>
                  <p>You will receive your own private Authority Portrait. Your organization will receive combined insights across participating employees. It will not receive your individual answers or personal Authority Portrait.</p>
                  <p>The assessment contains 105 statements and takes approximately 45 to 60 minutes to complete. Your progress will be saved if you need to return.</p>
                </>
              )}
              {match.participant_config?.welcome?.contact && (
                <p className="text-xs text-[#6B7280]">Questions? {match.participant_config.welcome.contact}</p>
              )}
              <button data-testid="welcome-continue" onClick={() => navigate("/consent")}
                className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2">
                Continue to Before You Begin
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
